import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Container as ContainerBase } from "components/misc/Layouts.js";
import { Typography, Paper, Divider } from "@mui/material";
import { motion } from "framer-motion";
import BgTitle from "components/title/BgTitle";

// Styled Components
const Container = tw(ContainerBase)`relative py-10 md:py-16`;
const ContentWrapper = styled(motion.div)`
  ${tw`max-w-6xl mx-auto`}
`;

const StyledPaper = styled(Paper)`
  ${tw`p-0 mt-6 rounded-lg shadow-lg overflow-hidden`}
  border-left: 2px solid #0c4d96;
`;

const HeaderSection = styled.div`
  ${tw`p-6 md:p-8 bg-white`}
`;

const ContentSection = styled.div`
  ${tw`p-6 md:p-8 bg-gray-100`}
  background-color: #f8f9fa;
`;

const DateText = styled.div`
  ${tw`text-sm text-gray-600 mb-2 flex items-center`}
  &:before {
    content: "";
    display: inline-block;
    width: 10px;
    height: 10px;
    ${tw`bg-blue-600 mr-2 rounded-full`}
  }
`;

const AnnouncementContent = styled.div`
  ${tw`leading-relaxed whitespace-pre-line`}
  font-size: 1rem;
  line-height: 1.8;
`;

// Helper function to format paragraphs with proper spacing
const FormattedContent = ({ content }) => {
  // Split the content by newline characters and render each paragraph
  const paragraphs = content.split("\n");

  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <Typography
          key={index}
          variant="body1"
          paragraph={index < paragraphs.length - 1} // Add paragraph margin except for last item
          sx={{
            lineHeight: 1.8,
            fontSize: { xs: "0.95rem", md: "1rem" },
            color: "#333",
            marginBottom: paragraph === "" ? "1rem" : "0rem", // Add more space for empty lines
          }}
        >
          {paragraph}
        </Typography>
      ))}
    </>
  );
};

const WebsiteRenewalAnnouncement = () => {
  const { t } = useTranslation();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <Container>
      <ContentWrapper
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Title */}
        <motion.div variants={itemVariants}>
          <BgTitle text={t("announcement.heading")} />
        </motion.div>

        {/* Announcement Card */}
        <motion.div variants={itemVariants}>
          <StyledPaper elevation={3}>
            {/* Header Section with Title and Date */}
            <HeaderSection>
              <DateText>
                {t("announcement.april-25-corporate-site-release.date")}
              </DateText>
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  fontWeight: "600",
                  color: "#0c4d96",
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  lineHeight: 1.3,
                  letterSpacing: "-0.01em",
                }}
              >
                {t("announcement.april-25-corporate-site-release.title")}
              </Typography>
            </HeaderSection>

            <Divider />

            {/* Content Section */}
            <ContentSection>
              <AnnouncementContent>
                <FormattedContent
                  content={t(
                    "announcement.april-25-corporate-site-release.content"
                  )}
                />
              </AnnouncementContent>
            </ContentSection>
          </StyledPaper>
        </motion.div>
      </ContentWrapper>
    </Container>
  );
};

export default WebsiteRenewalAnnouncement;
